/**
 * ToolCallStream — live micro-feed UI for the agent's tool-use loop.
 *
 * Renders one card per tool invocation as `progress` and `tool_completed`
 * SSE events arrive. Each step has a status:
 *   running → spinner + active label
 *   done    → green check + past-tense label
 *   error   → red alert + error message
 *
 * Port of secretary-pwa's ToolCallStream.jsx adapted to our styling. Used
 * inside `Conversation` while a stream is in-flight.
 */

import { Sparkles, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { toolLabel, formatParams } from '@/lib/toolLabels';
import type { ToolStep } from '@/types/chat';

interface ToolCallStreamProps {
  steps: ToolStep[];
  /** Optional generic "thinking" message shown when no steps yet. */
  thinkingStage?: string;
}

export function ToolCallStream({ steps, thinkingStage }: ToolCallStreamProps) {
  return (
    <div className="flex gap-2.5 mb-3">
      <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
        <Sparkles className="w-3.5 h-3.5 text-primary" />
      </div>
      <div className="px-3 py-2 bg-card border border-border rounded-2xl rounded-bl-md space-y-1.5 min-w-[200px] max-w-[420px]">
        {steps.length === 0 ? (
          <PendingDots stage={thinkingStage} />
        ) : (
          steps.map((step, i) => (
            <ToolStepRow
              key={`${step.step ?? i}-${step.tool ?? 'tool'}`}
              step={step}
            />
          ))
        )}
      </div>
    </div>
  );
}

function PendingDots({ stage }: { stage?: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        <Dot delay={0} />
        <Dot delay={150} />
        <Dot delay={300} />
      </div>
      {stage && stage !== 'Thinking...' && (
        <span className="text-xs text-muted-foreground">{stage}</span>
      )}
    </div>
  );
}

function Dot({ delay }: { delay: number }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce inline-block"
      style={{ animationDelay: `${delay}ms` }}
    />
  );
}

function ToolStepRow({ step }: { step: ToolStep }) {
  const status = step.status || 'running';
  const resolved = resolveLabel(step, status);
  const detailLine = step.error || resolved.detail || formatParams(step.paramsSummary);

  return (
    <div className="flex items-start gap-2 text-xs">
      <StatusIcon status={status} />
      <div className="min-w-0 flex-1">
        <div
          className={clsx(
            status === 'error'
              ? 'text-destructive font-medium'
              : status === 'done'
              ? 'text-muted-foreground'
              : 'text-foreground',
          )}
        >
          {resolved.label}
        </div>
        {detailLine && (
          <div
            className={clsx(
              'text-[10px] mt-0.5 truncate',
              status === 'error' ? 'text-destructive/80' : 'text-muted-foreground/80',
            )}
            title={detailLine}
          >
            {detailLine}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: ToolStep['status'] }) {
  if (status === 'done')
    return <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0 mt-[2px]" />;
  if (status === 'error')
    return <AlertCircle className="w-3 h-3 text-destructive shrink-0 mt-[2px]" />;
  return <Loader2 className="w-3 h-3 text-primary animate-spin shrink-0 mt-[2px]" />;
}

function resolveLabel(step: ToolStep, status: ToolStep['status']) {
  if (step.label) return { label: step.label, detail: step.detail ?? null };
  const labelState: 'active' | 'done' = status === 'running' ? 'active' : 'done';
  const params =
    step.params && Object.keys(step.params).length > 0
      ? step.params
      : step.paramsSummary || {};
  return toolLabel(step.tool || 'tool', params, labelState);
}
